import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import styled from 'styled-components';
import { Modal } from '@shared/ui/Modal';
import { Button } from '@shared/ui/Button';
import { Field, Label } from '@shared/ui/Input';
import { FallbackBanner } from '@shared/ui/FallbackBanner';
import { KadryFieldHint, KadryFormActions, KadryTextArea } from './form';

const Text = styled.div`
  font-size: 14px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Gap = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

interface Props {
  open: boolean;
  title: string;
  /** Что произойдёт (обычный текст). */
  description?: ReactNode;
  /** Необратимость/риски — выделенный блок-предупреждение. */
  warning?: ReactNode;
  confirmLabel: string;
  confirmVariant?: 'primary' | 'danger';
  /** Подпись поля причины/комментария. */
  reasonLabel?: string;
  reasonPlaceholder?: string;
  reasonHint?: ReactNode;
  /** Причина обязательна (кнопка неактивна, пока поле пустое). */
  reasonRequired?: boolean;
  reasonMaxLength?: number;
  busy?: boolean;
  /** Ошибка последней попытки (показывается над кнопками). */
  errorSlot?: ReactNode;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

/**
 * Подтверждение действия с причиной/комментарием: переходы статусов (причина
 * уходит в историю), переключение функций (основание — в аудит) и т.п.
 */
export function KadryReasonDialog({
  open,
  title,
  description,
  warning,
  confirmLabel,
  confirmVariant = 'primary',
  reasonLabel = 'Причина',
  reasonPlaceholder,
  reasonHint,
  reasonRequired = false,
  reasonMaxLength = 1000,
  busy = false,
  errorSlot,
  onConfirm,
  onCancel,
}: Props) {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const trimmed = reason.trim();
  const canSubmit = !busy && (!reasonRequired || trimmed.length > 0);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (canSubmit) onConfirm(trimmed);
  }

  function close() {
    if (!busy) onCancel();
  }

  return (
    <Modal open={open} title={title} onClose={close}>
      <form onSubmit={submit}>
        {description && <Text>{description}</Text>}
        {warning && (
          <Gap>
            <FallbackBanner tone="warning">{warning}</FallbackBanner>
          </Gap>
        )}
        <Field>
          <Label htmlFor="kadry-reason">
            {reasonLabel}
            {reasonRequired ? ' *' : ' (необязательно)'}
          </Label>
          <KadryTextArea
            id="kadry-reason"
            value={reason}
            maxLength={reasonMaxLength}
            placeholder={reasonPlaceholder}
            onChange={(e) => setReason(e.target.value)}
            disabled={busy}
            autoFocus
          />
          {reasonHint && <KadryFieldHint>{reasonHint}</KadryFieldHint>}
        </Field>
        {errorSlot && <Gap>{errorSlot}</Gap>}
        <KadryFormActions>
          <Button type="button" $variant="secondary" onClick={close} disabled={busy}>
            Отмена
          </Button>
          <Button type="submit" $variant={confirmVariant} disabled={!canSubmit}>
            {busy ? 'Сохраняем…' : confirmLabel}
          </Button>
        </KadryFormActions>
      </form>
    </Modal>
  );
}
